import {
    EventOutlined,
    FlagOutlined,
    HourglassEmptyOutlined,
    PersonOutlineRounded,
    PlaceOutlined,
    ScheduleOutlined,
} from '@mui/icons-material';
import moment from 'moment';
import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { SCHOOLS_IDS_BS, THEME_VERSIONS } from '../../resources/constants';
import { decimalToTime, getCurrentThemeVersion, getLessonNameFromType } from '../../resources/utils';
import { ApplicationState } from '../../store/types';
import { Lesson, Option } from '../../types/booking';
import { buildParticipants } from '../cart/cartLesson';
import LessonCard, { InfoCell, OptionRow } from '../cart/lessonCard';

/**
 * Read-only claim/history lesson card - maps a `Lesson` (Booking-Corner shape, with
 * capitalized fields) into the shared `LessonCard` view-model. No price breakdown,
 * no delete action and no problem state (these are confirmed/booked lessons).
 */
type ReduxProps = ConnectedProps<typeof connector>;

interface IProps {
    lesson: Lesson;
    schoolName: string;
}

type Props = IProps & WrappedComponentProps & ReduxProps;

function reducerDurationLesson(p: number, c: any): number {
    return moment.duration(moment(c.End, 'YYYY-MM-DDTHH:mm:ss').diff(moment(c.Start, 'YYYY-MM-DDTHH:mm:ss'))).asHours() + p;
}

function ClaimLesson(props: Props) {
    const {
        lesson, intl, schoolName, currentSchool,
    } = props;

    // ----- Hallenbarter: option prices are hidden for this company (preserved) -----
    const hallenbarterProductsSchoolId = Number(SCHOOLS_IDS_BS.HALLENBARTER[1]);
    const hallenbarterSchoolPath = String(SCHOOLS_IDS_BS.HALLENBARTER[0]).toLowerCase();
    const hallenbarterSkiSchoolId = 156;
    const rawLessonProductsSchoolId = (lesson as any).SchoolId ?? lesson.school_id?.external_id;
    const lessonProductsSchoolId = rawLessonProductsSchoolId !== undefined && rawLessonProductsSchoolId !== null
        ? Number(rawLessonProductsSchoolId)
        : undefined;
    const isHallenbarterCompany = lessonProductsSchoolId === hallenbarterProductsSchoolId
        || (currentSchool?.path || '').toLowerCase().includes(hallenbarterSchoolPath)
        || Number(currentSchool?.int_id) === hallenbarterSkiSchoolId
        || getCurrentThemeVersion() === THEME_VERSIONS.BookingsportHallenBarter;

    const {
        From, To, Price, Remark, Prestation, NbDays, NbParticipants,
        Participants, Instructor, MeetingPoint, Options, Split, Times,
    } = lesson;

    // ----- hours / times (reused from the original logic) -----
    let hours = '';
    let totalHours = '';
    let isNotEmptyHours = false;
    let startTime = '';
    let endTime = '';
    let isNotEmptyStartTime = false;
    let isNotEmptyEndTime = false;

    if (Split && Times) {
        const durationLesson = Times.reduce(reducerDurationLesson, 0.0);
        const durationTotalLesson = durationLesson * parseInt(NbDays, 10);
        isNotEmptyHours = (durationLesson !== 0);
        hours = `${decimalToTime(durationLesson, intl.formatMessage, false)}  ${intl.formatMessage({ id: 'booking.hoursDay' })}`;
        totalHours = `${decimalToTime(durationTotalLesson, intl.formatMessage, false)} ${intl.formatMessage({ id: 'booking.hoursTotal' })}`;
    } else {
        startTime = moment(From).format('HH[:]mm');
        isNotEmptyStartTime = (startTime !== '00:00');
        endTime = moment(To).format('HH[:]mm');
        isNotEmptyEndTime = (endTime !== '00:00');
        const durationLesson = moment.duration(moment(endTime, 'HH:mm').diff(moment(startTime, 'HH:mm'))).asHours();
        const durationTotalLesson = durationLesson * parseInt(NbDays, 10);
        isNotEmptyHours = (durationLesson !== 0);
        hours = `${decimalToTime(durationLesson, intl.formatMessage, false)}  ${intl.formatMessage({ id: 'booking.hoursDay' })}`;
        totalHours = `${decimalToTime(durationTotalLesson, intl.formatMessage, false)} ${intl.formatMessage({ id: 'booking.hoursTotal' })}`;
    }

    // ----- info grid -----
    const info: InfoCell[] = [
        { icon: <PlaceOutlined />, label: intl.formatMessage({ id: 'products.location' }), value: schoolName },
        { icon: <EventOutlined />, label: intl.formatMessage({ id: 'booking.startDate' }), value: `(${moment(From).format('dd')}) ${moment(From).format('DD.MM.YYYY')}` },
        { icon: <EventOutlined />, label: intl.formatMessage({ id: 'booking.endDate' }), value: `(${moment(To).format('dd')}) ${moment(To).format('DD.MM.YYYY')}` },
    ];
    if (!Split && isNotEmptyStartTime) {
        info.push({ icon: <ScheduleOutlined />, label: intl.formatMessage({ id: 'booking.startTime' }), value: startTime });
    }
    if (!Split && isNotEmptyEndTime) {
        info.push({ icon: <ScheduleOutlined />, label: intl.formatMessage({ id: 'booking.endTime' }), value: endTime });
    }
    if (Times && Times.length > 0) {
        info.push({
            icon: <ScheduleOutlined />,
            label: intl.formatMessage({ id: 'booking.hoursDetails' }),
            value: Times.map((t) => `${moment(t.Start, 'YYYY-MM-DDTHH:mm:ss').format('HH[:]mm')} - ${moment(t.End, 'YYYY-MM-DDTHH:mm:ss').format('HH[:]mm')}`).join(' · '),
        });
    }
    if (isNotEmptyHours) {
        info.push({
            icon: <HourglassEmptyOutlined />, label: intl.formatMessage({ id: 'booking.hours' }), value: hours, sub: totalHours
        });
    }
    if (MeetingPoint) {
        info.push({ icon: <FlagOutlined />, label: intl.formatMessage({ id: 'booking.meeting' }), value: MeetingPoint });
    }
    if (Instructor) {
        info.push({ icon: <PersonOutlineRounded />, label: intl.formatMessage({ id: 'booking.freeInstructors' }), value: `${Instructor}` });
    }

    // ----- participants (dynamic fields, empty ones hidden) -----
    const { columns: participantColumns, rows: participantRows } = buildParticipants(Participants || [], intl);
    const participants = participantRows.length > 0
        ? {
            count: participantRows.length,
            total: Number.isFinite(Number(NbParticipants)) ? Number(NbParticipants) : undefined,
            columns: participantColumns,
            rows: participantRows,
        }
        : undefined;

    // ----- options (price hidden for Hallenbarter) -----
    const showPrices = !isHallenbarterCompany;
    const optRows: OptionRow[] = (Options || []).map((o: Option) => ({
        text: o.Text,
        total: showPrices ? `CHF ${Number.isFinite(o.Price) ? o.Price.toFixed(2) : '0.00'}` : undefined,
    }));
    const options = optRows.length > 0 ? { rows: optRows } : undefined;

    return (
        <LessonCard
            intl={intl}
            model={{
                typePill: getLessonNameFromType(lesson.lesson_type, intl.formatMessage),
                durationPill: `${NbDays}${intl.formatMessage({ id: `booking.day${NbDays > 1 ? 's' : ''}` })}`,
                title: Prestation,
                price: { total: `CHF ${Price}.–` },
                info,
                participants,
                options,
                remark: Remark || undefined,
            }}
        />
    );
}

const mapStateToProps = (store: ApplicationState) => ({
    currentSchool: store.navigation.currentSchool,
});
const connector = connect(mapStateToProps);

export default injectIntl(connector(ClaimLesson));
